// Minimal Mattermost REST v4 client for the S2 spike — injectable `fetch` so
// tests run fully offline against captured fixtures (no real network).
// Deliberately narrow: only the calls S2 needs (probe/post/edit/reply/read
// thread/poll). Supervisor holds the bot token; this client never logs it
// (see mattermost-retry-classifier.ts for the retry policy consumed here).
import {
  classifyMattermostResponseStatus,
  type RetryClassification,
} from "./mattermost-retry-classifier.js";

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface MattermostClientOptions {
  baseUrl: string;
  botToken: string;
  fetchImpl?: FetchLike;
  timeoutMs?: number;
}

export interface MattermostUser {
  id: string;
  username: string;
  is_bot?: boolean;
}

export interface MattermostChannel {
  id: string;
  team_id: string;
  name: string;
  display_name?: string;
  type?: string;
}

export interface MattermostChannelMember {
  channel_id: string;
  user_id: string;
  roles?: string;
}

export interface MattermostPost {
  id: string;
  create_at: number;
  update_at: number;
  edit_at?: number;
  user_id: string;
  channel_id: string;
  root_id: string;
  message: string;
  props?: Record<string, unknown>;
}

export interface MattermostPostList {
  order: string[];
  posts: Record<string, MattermostPost>;
  next_post_id?: string;
  prev_post_id?: string;
}

export class MattermostApiError extends Error {
  status: number;
  classification: RetryClassification;

  constructor(status: number, message: string) {
    super(message);
    this.name = "MattermostApiError";
    this.status = status;
    this.classification = classifyMattermostResponseStatus(status);
  }
}

const DEFAULT_TIMEOUT_MS = 10_000;

// Thin REST client. Every call times out independently (per-call
// AbortController, not shared) per phase spec ("timeout riêng mỗi call").
export class MattermostRestClient {
  private readonly baseUrl: string;
  private readonly botToken: string;
  private readonly fetchImpl: FetchLike;
  private readonly timeoutMs: number;

  constructor(options: MattermostClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.botToken = options.botToken;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    // CXR-08: the deadline covers the body too — a server that sends headers
    // and then stalls the body must not outlive `timeoutMs`.
    try {
      const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${this.botToken}`,
          "Content-Type": "application/json",
          ...(init?.headers ?? {}),
        },
        signal: controller.signal,
      });
      if (!res.ok) {
        // Never interpolate the token into the error message — status + path
        // shape only, matching the "no token in logs" rule.
        throw new MattermostApiError(res.status, `Mattermost REST ${path} failed: ${res.status}`);
      }
      return (await res.json()) as T;
    } finally {
      clearTimeout(timer);
    }
  }

  getMe(): Promise<MattermostUser> {
    return this.request<MattermostUser>("/api/v4/users/me");
  }

  getChannelByNameInTeam(teamId: string, channelName: string): Promise<MattermostChannel> {
    return this.request<MattermostChannel>(
      `/api/v4/teams/${teamId}/channels/name/${encodeURIComponent(channelName)}`,
    );
  }

  getChannelMember(channelId: string, userId: string): Promise<MattermostChannelMember> {
    return this.request<MattermostChannelMember>(`/api/v4/channels/${channelId}/members/${userId}`);
  }

  createPost(
    channelId: string,
    message: string,
    rootId?: string,
    props?: Record<string, unknown>,
  ): Promise<MattermostPost> {
    return this.request<MattermostPost>("/api/v4/posts", {
      method: "POST",
      body: JSON.stringify({
        channel_id: channelId,
        message,
        root_id: rootId ?? "",
        ...(props ? { props } : {}),
      }),
    });
  }

  updatePost(postId: string, message: string): Promise<MattermostPost> {
    return this.request<MattermostPost>(`/api/v4/posts/${postId}/patch`, {
      method: "PUT",
      body: JSON.stringify({ message }),
    });
  }

  getPostThread(postId: string): Promise<MattermostPostList> {
    return this.request<MattermostPostList>(`/api/v4/posts/${postId}/thread`);
  }

  getPostsForChannel(
    channelId: string,
    page: number,
    perPage: number,
    since?: number,
  ): Promise<MattermostPostList> {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (since !== undefined) params.set("since", String(since));
    return this.request<MattermostPostList>(
      `/api/v4/channels/${channelId}/posts?${params.toString()}`,
    );
  }

  searchPosts(teamId: string, terms: string): Promise<MattermostPostList> {
    return this.request<MattermostPostList>(`/api/v4/teams/${teamId}/posts/search`, {
      method: "POST",
      body: JSON.stringify({ terms, is_or_search: false }),
    });
  }
}
